import { useEffect } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Api } from '../lib/api';
import { useUi } from '../store/ui';

export const qk = {
  note: (id: string) => ['note', id] as const,
  tree: ['tree'] as const,
  status: ['status'] as const,
  types: ['types'] as const,
  drift: ['drift'] as const,
  backlinks: (id: string) => ['backlinks', id] as const,
  unlinked: (id: string) => ['unlinked', id] as const,
  search: (q: string, opts: Record<string, unknown>) => ['search', q, opts] as const,
};

export function useNote(id: string | null) {
  return useQuery({ queryKey: qk.note(id ?? ''), queryFn: () => Api.notes.get(id!), enabled: !!id, staleTime: 5_000 });
}
export function useTree() {
  return useQuery({ queryKey: qk.tree, queryFn: Api.vault.tree, staleTime: 30_000 });
}
export function useStatus() {
  return useQuery({ queryKey: qk.status, queryFn: Api.vault.status, refetchInterval: 10_000, staleTime: 5_000 });
}
export function useTypes() {
  return useQuery({ queryKey: qk.types, queryFn: Api.types.list, staleTime: 60_000 });
}
export function useBacklinks(id: string | null) {
  return useQuery({ queryKey: qk.backlinks(id ?? ''), queryFn: () => Api.notes.backlinks(id!), enabled: !!id, staleTime: 5_000 });
}
export function useUnlinked(id: string | null) {
  return useQuery({ queryKey: qk.unlinked(id ?? ''), queryFn: () => Api.notes.unlinked(id!), enabled: !!id, staleTime: 10_000 });
}
export function useSearch(q: string, opts: { limit?: number; prefix?: boolean; type?: string; tag?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: qk.search(q, opts),
    queryFn: () => Api.search(q, opts),
    enabled: enabled && q.trim().length > 0,
    placeholderData: keepPreviousData, // results stream in; never a spinner
    staleTime: 5_000,
  });
}

/**
 * Subscribe to vault change events. Invalidates the right queries and keeps
 * open notes in sync with edits made outside the app.
 */
export function useVaultEvents(): void {
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  useEffect(() => {
    let es: EventSource | null = null;
    let retry = 1000;
    let closed = false;
    const connect = () => {
      es = new EventSource(Api.eventsUrl());
      es.onopen = () => {
        retry = 1000;
      };
      es.addEventListener('indexed', (e) => {
        const d = JSON.parse((e as MessageEvent).data) as { path: string; outcome: string; ours: boolean };
        qc.invalidateQueries({ queryKey: qk.tree });
        qc.invalidateQueries({ queryKey: ['search'] });
        qc.invalidateQueries({ queryKey: ['backlinks'] });
        qc.invalidateQueries({ queryKey: ['unlinked'] });
        if (d.outcome === 'indexed' && !d.ours) {
          // Someone (or something) else changed a note. Refresh whichever open note has that path.
          for (const q of qc.getQueryCache().findAll({ queryKey: ['note'] })) {
            const data = q.state.data as { path?: string } | undefined;
            if (data?.path === d.path) qc.invalidateQueries({ queryKey: q.queryKey });
          }
        }
      });
      es.addEventListener('removed', () => {
        qc.invalidateQueries({ queryKey: qk.tree });
        qc.invalidateQueries({ queryKey: ['search'] });
        qc.invalidateQueries({ queryKey: ['backlinks'] });
      });
      es.onerror = () => {
        es?.close();
        if (closed) return;
        setTimeout(connect, retry);
        retry = Math.min(retry * 2, 15_000);
      };
    };
    connect();
    return () => {
      closed = true;
      es?.close();
    };
  }, [qc, toast]);
}
